import React, { useState } from 'react';
import { 
  Globe, Shield, FileText, Bell, Save, RotateCcw, Copy, 
  Moon, Check, Settings
} from 'lucide-react';

const AGREEMENT_TEMPLATE = `I, {{NAME}} , working at {{CENTER}} Center, hereby agree to the following terms and conditions set by the Company. I understand that my salary will be based on student admissions, assigned students, student learning performance, and course completion as per Company policy. I agree to strictly follow all Company software systems, operational guidelines, and management instructions.

and future assignments.

1. HRMSSECURE (Attendance, Task & Leave Management System)

HRMSSECURE is the official Attendance, Task, and Leave Management System of the Company. The Employee agrees to strictly comply with the following:

1.1 Attendance Rules

1. Attendance must be marked strictly according to assigned Shift Start Time and Shift End Time.
2. A buffer of 10 minutes is allowed after shift start time.
3. Attendance marked beyond 10 minutes from shift start time shall automatically be treated as Half Day.
4. A buffer of 1 hour is allowed after shift end time for clock-out.
5. Failure to clock out within 1 hour after shift end time shall result in automatic marking of Half Day.

1.2 Task Compliance

1. All tasks assigned through HRMSSECURE must be completed within the deadline.
2. Daily Work Reports (DWR) must be submitted every working day before 8:00 PM.
3. Incomplete tasks and missed DWRs will directly impact performance evaluation.

1.3 Leave Policy

1. All leave requests must be submitted via HRMSSECURE in advance.
2. Unapproved leaves will be considered Loss of Pay (LOP).
3. Leaves can only be taken as per the Company's approved leave policy.

2. Responsibilities

The Employee agrees to:
- Maintain professional conduct at all times.
- Keep company data and employee information confidential.
- Report any technical or operational issues immediately to the manager.`;

const SIDEBAR_TABS = [
  { id: 'general', label: 'General', icon: Globe },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'agreement', label: 'Agreement Template', icon: FileText },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('general');
  const [darkMode, setDarkMode] = useState(false);
  const [companyName, setCompanyName] = useState('Secure Insights');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [timezone, setTimezone] = useState('Asia/Kolkata (IST)');
  const [templateText, setTemplateText] = useState(AGREEMENT_TEMPLATE);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  const handleCopyLink = () => {
    const link = 'https://hrmssecure.in/employee-services-form';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy using clipboard API', err);
        fallbackCopyTextToClipboard(link);
      });
    } else {
      fallbackCopyTextToClipboard(link);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Ensure the textarea is off-screen
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Fallback copy failed', err);
    }

    document.body.removeChild(textArea);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return;
    }
    try {
      const api = (await import('../api')).default;
      await api.post('/user/update-password', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      setPwSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to update password. Check current password.');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-black text-slate-800 tracking-tight">Platform Settings</h1>
        <p className="text-[13px] font-semibold text-blue-500 mt-1">Manage security protocols, preferences, and system configurations.</p>
      </div>

      {/* Main Settings Layout */}
      <div className="flex gap-6 min-h-[600px]">
        
        {/* Left Sidebar */}
        <div className="w-[200px] flex-shrink-0">
          <nav className="flex flex-col gap-1">
            {SIDEBAR_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-[13px] font-bold text-left transition-all w-full
                  ${activeSection === tab.id 
                    ? 'text-[#10b981] border-l-[3px] border-[#10b981] bg-emerald-50/30 pl-[13px]' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 border-l-[3px] border-transparent'
                  }`}
              >
                <tab.icon size={16} className="flex-shrink-0" strokeWidth={2} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content Panel */}
        <div className="flex-1 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          
          {/* ─── GENERAL ─── */}
          {activeSection === 'general' && (
            <div className="p-8 space-y-8">
              
              {/* Company Info */}
              <div>
                <h2 className="text-[17px] font-black text-slate-800 mb-5">Company Information</h2>
                
                <div className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-bold text-[#10b981]">Company Name</label>
                    <input 
                      type="text" 
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                    />
                    <p className="text-[11px] font-semibold text-[#10b981]">Updates globally in real-time.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[12px] font-bold text-slate-600">Contact Email</label>
                      <input 
                        type="email" 
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                        placeholder="admin@company.com"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[12px] font-bold text-slate-600">Contact Phone</label>
                      <input 
                        type="tel" 
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[12px] font-bold text-slate-600">Company Address</label>
                    <textarea 
                      rows="3"
                      value={companyAddress}
                      onChange={(e) => setCompanyAddress(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm resize-none transition-colors"
                      placeholder="Enter full company address..."
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Appearance & Region */}
              <div>
                <h2 className="text-[17px] font-black text-slate-800 mb-5">Appearance & Region</h2>
                
                {/* Dark Mode Toggle */}
                <div className="border border-slate-200 rounded-lg p-4 flex items-center justify-between mb-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
                      <Moon size={18} strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-700">Dark Mode</p>
                      <p className="text-[11px] font-semibold text-slate-400">Enable dark theme for the application.</p>
                    </div>
                  </div>
                  {/* Toggle Switch */}
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className={`relative w-11 h-6 rounded-full transition-colors shadow-inner ${darkMode ? 'bg-[#10b981]' : 'bg-slate-200'}`}
                  >
                    <span 
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${darkMode ? 'translate-x-5' : 'translate-x-0'}`}
                    ></span>
                  </button>
                </div>

                {/* Timezone */}
                <div className="space-y-1.5">
                  <label className="text-[12px] font-bold text-slate-600">Timezone</label>
                  <select 
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px]"
                  >
                    <option>Asia/Kolkata (IST)</option>
                    <option>UTC</option>
                    <option>America/New_York (EST)</option>
                    <option>Europe/London (GMT)</option>
                  </select>
                </div>

              </div>

              {/* Save */}
              <div>
                <button 
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#10b981] hover:bg-emerald-600 text-white font-bold text-[13px] rounded-lg transition-colors shadow-sm shadow-emerald-500/20"
                >
                  {saved ? <Check size={16} strokeWidth={3} /> : <Save size={16} strokeWidth={2.5} />}
                  {saved ? 'Saved!' : 'Save Changes'}
                </button>
              </div>

            </div>
          )}

          {/* ─── SECURITY ─── */}
          {activeSection === 'security' && (
            <div className="p-8 space-y-6">
              <h2 className="text-[17px] font-black text-slate-800">Security Settings</h2>
              
              {pwError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-[12px] font-semibold px-4 py-3 rounded-lg">
                  {pwError}
                </div>
              )}
              {pwSuccess && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-semibold px-4 py-3 rounded-lg">
                  Password updated successfully!
                </div>
              )}

              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[12px] font-bold text-slate-600">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[12px] font-bold text-slate-600">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[12px] font-bold text-slate-600">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    className="w-full border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm transition-colors"
                  />
                </div>

                <div className="border border-slate-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-bold text-slate-700">Two-Factor Authentication</p>
                      <p className="text-[11px] font-semibold text-slate-400 mt-0.5">Add an extra layer of security to your account.</p>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-400 px-2 py-1 rounded">Disabled</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#10b981] hover:bg-emerald-600 text-white font-bold text-[13px] rounded-lg transition-colors shadow-sm shadow-emerald-500/20"
                >
                  {pwSuccess ? <Check size={16} strokeWidth={3} /> : <Save size={16} strokeWidth={2.5} />}
                  {pwSuccess ? 'Password Updated!' : 'Update Password'}
                </button>
              </form>
            </div>
          )}

          {/* ─── AGREEMENT TEMPLATE ─── */}
          {activeSection === 'agreement' && (
            <div className="p-8 space-y-6">
              <div>
                <h2 className="text-[17px] font-black text-slate-800">Employee Agreement Template</h2>
                <p className="text-[12px] font-semibold text-slate-500 mt-1">
                  Edit the terms and conditions that employees must accept. Use <span className="text-indigo-500 font-black">{'{{NAME}}'}</span> and <span className="text-indigo-500 font-black">{'{{CENTER}}'}</span> as placeholders for dynamic data.
                </p>
              </div>

              {/* Rich Text-style Toolbar */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                  <select className="border border-slate-200 rounded text-[11px] font-bold text-slate-600 py-1 px-2 focus:outline-none bg-white">
                    <option>Normal</option>
                    <option>Heading 1</option>
                    <option>Heading 2</option>
                  </select>
                  <div className="w-px h-5 bg-slate-200 mx-1"></div>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] font-black text-slate-600" title="Bold">B</button>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] italic font-bold text-slate-600" title="Italic">I</button>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-600 underline" title="Underline">U</button>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500 line-through" title="Strikethrough">S</button>
                  <div className="w-px h-5 bg-slate-200 mx-1"></div>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-600" title="Bullet List">≡</button>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-600" title="Numbered List">≔</button>
                  <div className="w-px h-5 bg-slate-200 mx-1"></div>
                  <button className="w-7 h-7 rounded hover:bg-slate-200 flex items-center justify-center text-slate-500" title="Clear">
                    <span className="text-[11px] font-bold">Tx</span>
                  </button>
                </div>

                <textarea
                  rows="18"
                  value={templateText}
                  onChange={(e) => setTemplateText(e.target.value)}
                  className="w-full p-4 text-[13px] text-slate-700 font-medium leading-relaxed focus:outline-none resize-none border-0"
                  style={{ fontFamily: 'inherit' }}
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button 
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#10b981] hover:bg-emerald-600 text-white font-bold text-[13px] rounded-lg transition-colors shadow-sm shadow-emerald-500/20"
                >
                  {saved ? <Check size={16} strokeWidth={3} /> : <Save size={16} strokeWidth={2.5} />}
                  {saved ? 'Saved!' : 'Save Template'}
                </button>
                <button 
                  onClick={() => setTemplateText(AGREEMENT_TEMPLATE)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-600 font-bold text-[13px] rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                >
                  <RotateCcw size={16} /> Reset
                </button>
              </div>

              {/* Shareable Link */}
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-[14px] font-black text-slate-700 mb-1">Shareable Link for Employees</h3>
                <p className="text-[12px] font-semibold text-slate-400 mb-3">Send this link to employees. They will see the agreement template you saved above.</p>
                <div className="flex items-center gap-3">
                  <input 
                    type="text" 
                    readOnly 
                    value="https://hrmssecure.in/employee-services-form"
                    className="flex-1 border border-slate-200 rounded-lg py-2.5 px-3 text-[13px] font-medium text-slate-500 bg-slate-50/50 focus:outline-none shadow-sm"
                  />
                  <button 
                    onClick={handleCopyLink}
                    className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-[13px] font-bold transition-colors shadow-sm whitespace-nowrap ${copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {copied ? <Check size={16} strokeWidth={3} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>

            </div>
          )}

          {/* ─── NOTIFICATIONS ─── */}
          {activeSection === 'notifications' && (
            <div className="p-8 space-y-6">
              <h2 className="text-[17px] font-black text-slate-800">Notification Preferences</h2>
              
              {[
                { label: 'Leave Request Alerts', desc: 'Notify when an employee submits a leave request.', enabled: true },
                { label: 'Task Due Reminders', desc: 'Remind about overdue or upcoming task deadlines.', enabled: true },
                { label: 'Payroll Processing', desc: 'Alerts when payroll cycle is complete.', enabled: false },
                { label: 'Daily Work Reports', desc: 'Notify when DWRs are submitted or missed.', enabled: true },
                { label: 'New Employee Added', desc: 'Alert when a new employee is onboarded.', enabled: false },
              ].map((notif, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 shadow-sm flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-bold text-slate-700">{notif.label}</p>
                    <p className="text-[11px] font-semibold text-slate-400 mt-0.5">{notif.desc}</p>
                  </div>
                  <button 
                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors shadow-inner ${notif.enabled ? 'bg-[#10b981]' : 'bg-slate-200'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${notif.enabled ? 'translate-x-5' : 'translate-x-0'}`}></span>
                  </button>
                </div>
              ))}

              <button 
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#10b981] hover:bg-emerald-600 text-white font-bold text-[13px] rounded-lg transition-colors shadow-sm shadow-emerald-500/20"
              >
                {saved ? <Check size={16} strokeWidth={3} /> : <Save size={16} strokeWidth={2.5} />}
                {saved ? 'Saved!' : 'Save Preferences'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
